<template>
    <div class="flex flex-col justify-center items-center">
        <span
            class="text-6xl font-works-sans font-extrabold leading-12"
            v-text="isLogin ? t('titleLogin') : t('titleRegister')"
        ></span>

        <div class="my-4 leading-5 text-md">
            {{ t('with') }}
        </div>

        <div class="flex space-x-4 w-full">
            <BtnAuth
                data-testid="auth-form-google-btn"
                :disabled="isAuthInProgress"
                :loading="$store.getters['auth/googleAuthRequestInProgress']"
                icon-src="/images/icons/social/icon-google.svg"
                icon-alt="Google"
                class="flex-1"
                elevated
                type="button"
                @click="googleAuth"
            />

            <BtnAuth
                v-if="isLogin"
                data-testid="auth-form-facebook-btn"
                :disabled="isAuthInProgress"
                :loading="$store.getters['auth/facebookAuthRequestInProgress']"
                icon-src="/images/icons/social/icon-facebook-white.svg"
                icon-alt="Facebook"
                class="flex-1"
                color="brand-facebook"
                content-color="white"
                elevated
                type="button"
                @click="facebookAuth"
            />

            <BtnAuth
                id="custom-apple-button"
                data-testid="auth-form-apple-btn"
                :disabled="isAuthInProgress"
                :loading="$store.getters['auth/appleAuthRequestInProgress']"
                icon-src="/images/icons/social/icon-apple-white.svg"
                icon-alt="Apple"
                class="flex-1"
                color="black"
                content-color="white"
                elevated
                type="button"
                @click="appleAuth"
            />
        </div>

        <form
            class="block w-full"
            @submit.prevent
        >
            <div
                class="text-center my-4 leading-5 text-md"
                v-text="t('or')"
            ></div>
            <InputContainer
                v-slot="{ state }"
                :vuelidate="v$.email"
                :label="t('input.email.label')"
                class="w-full"
            >
                <InputField
                    v-model="email"
                    data-testid="auth-form-username-input"
                    type="email"
                    autocomplete="username"
                    :placeholder="t(`input.email.placeholder.${$data._type}`)"
                    :state="state"
                />
            </InputContainer>

            <InputContainer
                v-slot="{ state }"
                :vuelidate="v$.password"
                :label="t('input.password.label')"
                :feedback="v$.password.minLength?.$message"
                class="mb-1 w-full"
            >
                <InputField
                    v-model="password"
                    data-testid="auth-form-password-input"
                    type="password"
                    autocomplete="current-password"
                    :placeholder="t(`input.password.placeholder.${$data._type}`, { min: pwdLength })"
                    :state="state"
                />
            </InputContainer>

            <div
                v-if="isLogin"
                class="flex flex-wrap items-center justify-between w-full -mt-1"
            >
                <InputCheckbox
                    v-model="rememberMe"
                    data-testid="stay-loggged-in-checkbox"
                    class="mb-4 mr-4"
                >
                    {{ t('rememberMe') }}
                </InputCheckbox>
                <a
                    data-testid="auth-form-forgot-password"
                    :href="route('auth.forgot_password.index')"
                    class="mb-4 text-body-2 text-blue-600"
                >
                    {{ t('forgotPassword') }}
                </a>
            </div>

            <!-- native modifier has been removed, please confirm whether the function has been affected  -->

            <BtnCta
                :data-testid="authFormBtnTestId"
                class="mt-1 w-full"
                :label="isLogin ? submitTextLogin || t('loginCta') : submitTextRegister || t('registrationCta')"
                :loading="isAuthInProgress"
                :disabled="isAuthInProgress"
                type="submit"
                @click="emailAuth"
            />

            <!-- @slot Provide legal text for register forms -->
            <slot
                v-if="!isLogin"
                name="legal"
            >
                <p class="text-body-3 text-slate-400 mt-4 text-center">
                    <i18next :translation="t(legalTextPath || 'legalNotice')">
                        <!-- TO TEST IF: i18next will break if there is a space or newline between the elements -->
                        <template #termsAndConditions>
                            <a
                                data-testid="auth-form-terms-and-conditions-link"
                                :href="route('landing-page.show', { slug: t('footer.conditions_slug') })"
                                class="cursor-pointer text-blue-600"
                                target="_blank"
                            >
                                {{ t('termsAndConditions') }}
                            </a> </template
                        ><template #privacyPolicy>
                            <a
                                data-testid="auth-form-privacy-policy-link"
                                :href="route('landing-page.show', { slug: t('footer.privacy_slug') })"
                                class="cursor-pointer text-blue-600"
                                target="_blank"
                            >
                                {{ t('privacyPolicy') }}
                            </a>
                        </template>
                    </i18next>
                </p>
            </slot>

            <p class="mt-4 text-slate-600 text-center">
                <i18next :translation="t(isLogin ? 'noAccount' : 'loginHint')">
                    <template #link>
                        <a
                            data-testid="auth-form-register-or-login-link"
                            :class="{ 'text-blue-600 cursor-pointer': !isAuthInProgress }"
                            @click="!isAuthInProgress && toggleType()"
                            v-text="isLogin ? t('registerHint') : t('login')"
                        ></a>
                    </template>
                </i18next>
            </p>
        </form>
    </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import { useCookies } from '@vueuse/integrations/useCookies';
import route from '@router/route';
import { useVuelidate } from '@vuelidate/core';
import BtnAuth from '@components/BtnAuth';
import InputCheckbox from '@/components/InputCheckbox';
import { mapGetters } from 'vuex';
import { useServerValidation } from '@/composables/serverValidation';
import { pushGtmEvent, GtmEventName } from '@/utils/gtmUtils';
import { helpers } from '@vuelidate/validators';
import { email, required, withI18n } from '@/utils/validators';
import { Cookie } from '@/utils/cookieUtils';
import { LocalStorageKeys } from '@/utils/localStorageUtils';
import { AuthType } from '@components/AuthForm/AuthType';
import { trackEvent } from '@/modules/tracking';
import { updateUserStudyOnboarding } from '@api/gateway/legacy-api/study';
import { getGeolocationFromIp } from '@api/gateway/legacy-api/profile';
import { RouteGroup } from '@router/types';
import { useTranslation } from 'i18next-vue';

/**
 * This is our default authentication form used across the platform.
 * It provides login & registration functionality and you can specify which one should be displayed.
 * Form validation & authentication via some providers is included.
 */
export default {
    name: 'AuthForm',

    components: {
        BtnAuth,
        InputCheckbox,
    },

    props: {
        /**
         * Type of auth form.
         * @values login, register
         */
        type: {
            type: String,
            required: true,
            validator: (val) => Object.values(AuthType).includes(val),
        },
        /**
         * Text to override the default register cta text
         */
        submitTextRegister: {
            type: String,
            default: null,
        },
        /**
         * Text to override the default login cta text
         */
        submitTextLogin: {
            type: String,
            default: null,
        },
        /**
         * Alternative legal text displayed for registration.
         * Supports {{termsAndConditions}} and {{privacyPolicy}}
         */
        legalTextPath: {
            type: String,
            default: null,
        },
        /**
         * Additional options that are being passed to the auth requests
         */
        authOptions: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['update:type'],

    setup() {
        const { t } = useTranslation('authentication');
        return {
            cookies: useCookies(),
            v$: useVuelidate(),
            ...useServerValidation(),
            t,
        };
    },

    data() {
        return {
            RouteGroup,
            // eslint-disable-next-line vue/no-reserved-keys
            _type: this.$props.type,
            email: '',
            password: '',
            rememberMe: false,
            pwdLength: 8,
        };
    },

    validations() {
        const validators = {
            email: {
                required,
                email,
            },
            password: {
                required,
                minLength: withI18n(
                    helpers.withParams({ min: this.pwdLength }, (val) => val?.length >= this.pwdLength),
                ),
            },
        };

        delete validators.password[this.isLogin ? 'minLength' : 'required'];

        return validators;
    },

    computed: {
        ...mapGetters('auth', ['isAuthInProgress', 'isLoggedIn']),
        isLogin() {
            return this.$data._type === AuthType.LOGIN;
        },

        authFormBtnTestId() {
            if (this.isLogin) {
                if (this.authOptions?.['ke-onboarding']) {
                    return 'auth-form_btn-login-ce';
                }
                return 'auth-form_btn-login';
            }
            if (this.authOptions?.['ke-onboarding']) {
                return 'auth-form_btn-register-ce';
            }
            return 'auth-form_btn-register';
        },
    },

    watch: {
        type: {
            immediate: true,
            handler(val) {
                this.$data._type = val;
                if (val === AuthType.REGISTER) {
                    pushGtmEvent(GtmEventName.REGISTRATION_START, {
                        friend_invite: !!this.cookies.get(Cookie.USER_REFERRAL_ID),
                    });
                }
            },
        },
    },

    created() {
        this.$store.dispatch('auth/initializeFacebookAuthentication');
        this.$store.dispatch('auth/initializeAppleAuthentication');
    },
    methods: {
        route,
        googleAuth() {
            this.socialAuth('loginGoogleUser').then(this.onAuthSuccess);
        },
        facebookAuth() {
            this.socialAuth('sendFacebookAuthRequest').then(this.onAuthSuccess);
        },
        appleAuth() {
            this.socialAuth('loginAppleUser').then((data) => {
                if (data?.removeFromApple) {
                    this.$store.dispatch('flashMessages/showError', {
                        content: this.t('authentication:apple.remove'),
                    });
                    return;
                }

                this.onAuthSuccess(data);
            });
        },
        socialAuth(storeAction) {
            if (!this.$route.fullPath.includes(RouteGroup.COMPANIES_SEARCH)) {
                window.localStorage.setItem(LocalStorageKeys.REGISTRATION_ENTRY_POINT, this.$route.fullPath);
            }
            this.v$.$reset();

            return this.$store.dispatch(`auth/${storeAction}`, {
                ...this.authOptions,
                remember: this.isLogin ? this.rememberMe : undefined,

                action: this.$data._type,
            });
        },
        emailAuth() {
            if (!this.$route.fullPath.includes(RouteGroup.COMPANIES_SEARCH)) {
                window.localStorage.setItem(LocalStorageKeys.REGISTRATION_ENTRY_POINT, this.$route.fullPath);
            }
            this.v$.$reset();

            this.v$.$touch();
            if (this.v$.$invalid) return;

            const data = {
                ...this.authOptions,
                email: this.email,
                password: this.password,
                remember: this.isLogin ? this.rememberMe : undefined,
            };

            const action = this.isLogin ? 'auth/sendEmailLoginRequest' : 'auth/sendEmailRegistrationRequest';

            this.$store.dispatch(action, data).then(this.onAuthSuccess).catch(this.processServerError);
        },
        onAuthSuccess(data) {
            if (!data?.success) return;

            if (data.type === AuthType.REGISTER) {
                this.onRegisterSuccess(data);
            } else {
                this.onLoginSuccess();
            }
        },
        onLoginSuccess() {
            pushGtmEvent(GtmEventName.LOGIN_SUCCESS, {
                login_status: 'logged_in',
            });

            // if the user was logged in before it means this action
            // was dispatched due to an expired session -- we can
            // just close the authentication dialog in this case
            if (this.isLoggedIn) {
                this.$store.dispatch('ui/closeAuthOverlay');
                return;
            }

            this.finalizeAuthSuccess(AuthType.LOGIN);
        },
        async onRegisterSuccess(data) {
            pushGtmEvent(GtmEventName.REGISTRATION_SIGNUP, {
                user_type: 'regular_user',
                login_status: 'logged_in',
                friend_invite: data.is_referred,
            });

            setTimeout(async () => {
                const location = await getGeolocationFromIp().catch(() => {});

                // Priorities: DB data > Url parameter > IP data
                if (Object.keys(this.$route?.query).length) {
                    let prefillType = null;
                    if (this.$route?.query.is_pupil === '1' || this.$route?.query.is_pupil === '0') {
                        prefillType = 'parameter';
                    }
                    await this.handleOnboardingPayload({ ...this.$route.query, is_pupil_prefill_type: prefillType });
                } else if (location && location.country_name !== 'Germany') {
                    await this.handleOnboardingPayload({ is_pupil: '0', is_pupil_prefill_type: 'location' });
                }

                window.location.href = route('profile.register');
            }, 500);
        },
        /**
         * handles common auth success logic for both login & register
         */
        finalizeAuthSuccess(type) {
            const queryRedirect = this.$route?.query?.redirect;
            const redirectPath = queryRedirect?.startsWith('/')
                ? queryRedirect
                : localStorage.getItem(LocalStorageKeys.REGISTRATION_ENTRY_POINT) || route('dashboard.show');

            // remove entry point we set in emailAuth() or socialAuth()
            // in case of register we remove it in the ProfileRegisterContainer component (finishProcess())
            if (type === AuthType.LOGIN) {
                localStorage.removeItem(LocalStorageKeys.REGISTRATION_ENTRY_POINT);
            }

            window.location.href = redirectPath;
        },
        toggleType() {
            this.v$.$reset();
            this.v$.$clearExternalResults();

            const type = this.isLogin ? AuthType.REGISTER : AuthType.LOGIN;
            const trackingName = this.isLogin ? 'go_to_signup_button_click' : 'go_to_login_button_click';

            this.$data._type = type;

            /**
             * Updates the prop `type` when the auth form is being switched by the user
             */
            this.$emit('update:type', type);

            trackEvent('registration', trackingName);
        },
        async handleOnboardingPayload(payload) {
            let uniId = null;
            if (payload.is_pupil === '0') {
                if (payload.uni) {
                    uniId = payload.uni;
                }
            }
            if (payload.is_pupil === '1') {
                if (payload.school_type) {
                    uniId = payload.school_type;
                }
            }
            try {
                await updateUserStudyOnboarding({
                    is_pupil: payload?.is_pupil,
                    is_pupil_prefill_type: payload?.is_pupil_prefill_type,
                    university_id: uniId,
                    school_id: payload?.is_pupil === '1' && payload?.school_type ? payload?.school : null,
                    degree_type_id: null,
                    major_category_id: null,
                    major_id: null,
                    degree_program_id: null,
                    semester_id: null,
                });
            } catch (e) {
                /* empty: we don't care if this call fails or not.
                and the catch here is to avoid the user from being stuck at the current page if this call fails.
                if the user has a wrong input, the onboarding process will start from scratch */
            }
        },
    },
};
</script>
